import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Loader } from '@mantine/core';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import styles from './CreateContent.module.css';
import { uploadAudio } from '../../services/audioApi';
import { UploadAudioResponse } from '../../types';
import { useNotification } from '../../context/NotificationContext';
import { getApiUrl } from '../../config/api';
import { 
  fetchProjects, 
  Project, 
  deleteProject, 
  duplicateProject, 
  updateProjectStage,
  ProjectStage
} from '../../services/projectService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUpload, 
  faMicrophone, 
  faVideo, 
  faCalendarAlt, 
  faClock,
  faEllipsisVertical,
  faTrash,
  faDownload,
  faLink,
  faMagicWandSparkles
} from '@fortawesome/free-solid-svg-icons';
import Dashboard from '../Dashboard/Dashboard';
import { addAspectRatioConversionParams } from '../../utils/navigationHelpers';
import { useTranslation } from 'react-i18next';

const CreateContent: React.FC = () => {
  const { t } = useTranslation('contentCreation');
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [jobId, setJobId] = useState("");
  const [selectedOption, setSelectedOption] = useState<'all' | 'recent' | 'audio' | 'video'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceAudioRef = useRef<HTMLInputElement>(null);

  // Fetch projects when component mounts
  useEffect(() => {
    let isMounted = true;
    
    const loadProjects = async () => {
      try {
        setIsProjectsLoading(true);
        setError(null);
        
        if (!isMounted) return;
        
        const response = await fetchProjects(1, 1000);
        
        if (!isMounted) return;
        
        // If we get back response with projects property, use it even if empty
        if (response && 'projects' in response) {
          console.log('All projects from API:', response.projects.map(p => ({
            id: p.id,
            title: p.title,
            stage: p.stage
          })));
          
          // Filter for projects that have audio (Create Content is audio-based)
          const contentProjects = response.projects.filter(p => {
            const hasOriginalAudioUrl = p.originalAudioUrl && p.originalAudioUrl.trim() !== '';
            const hasFinalAudio = p.finalAudio && p.finalAudio.trim() !== '';
            const hasAudioContent = hasOriginalAudioUrl || hasFinalAudio;
            
            console.log(`Project ${p.id} (${p.title}) - Has Audio: ${hasAudioContent}`);
            return hasAudioContent;
          });
          
          setProjects(contentProjects);
          console.log('Filtered content creation projects:', 
            contentProjects.map(p => ({
              id: p.id,
              title: p.title,
              type: p.type,
              stage: p.stage
            }))
          );
        } else {
          // If API returns unexpected format, set empty array and show a message
          setProjects([]);
          console.warn('API returned unexpected data format');
        }
        
      } catch (err) {
        if (!isMounted) return;
        
        console.error('Error fetching projects:', err);
        setError('Failed to load projects. Please try again later.');
        showNotification({
          title: 'Error',
          message: 'Failed to load projects',
          type: 'error'
        });
        // Set empty projects array to prevent loading spinner from showing indefinitely
        setProjects([]);
      } finally {
        if (isMounted) {
          setIsProjectsLoading(false);
        }
      }
    };

    loadProjects();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  // Trigger hidden file input when "Upload Audio" is clicked
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let audioBlob: Blob;
    setIsLoading(true);
    
    if(event.target.files) {
      audioBlob = event.target.files[0];
    
      try {
        // Use the audio upload API instead of video
        const response = await uploadAudio(audioBlob);
        
        // If the audio upload API returns a job ID for tracking progress
        if (response && response.jobId) {
          setJobId(response.jobId);
        } else if (response && response.file && response.projectId) {
          // If it returns the result directly, process it
          processData(response.file.url, response.projectId);
        }
      } catch (error) {
        console.error("Upload failed:", error);
        showNotification({
          title: 'Upload Failed',
          message: 'There was an error uploading your audio file',
          type: 'error'
        });
        setIsLoading(false);
      }
    }
  };

  const processData = async (mediaUrl: string, projectId: string) => {
    setIsLoading(false);
    setUploadStatus('');
    
    console.log("Media URL---> ", mediaUrl);
    console.log("projectId---> ", projectId);
    
    try {
      const audioUrl = mediaUrl;
      const token = localStorage.getItem('authToken');
      
      // Update project with audio URL
      await fetch(getApiUrl(`/projects/${projectId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ originalAudioUrl: audioUrl })
      });
      
      // Refresh projects list
      const projectsResponse = await fetchProjects(1, 1000);
      if (projectsResponse && 'projects' in projectsResponse) {
        const contentProjects = projectsResponse.projects.filter(p => {
          const hasOriginalAudioUrl = p.originalAudioUrl && p.originalAudioUrl.trim() !== '';
          const hasFinalAudio = p.finalAudio && p.finalAudio.trim() !== '';
          return hasOriginalAudioUrl || hasFinalAudio;
        });
        setProjects(contentProjects);
      }
      
      // Update project stage
      await updateProjectStage(projectId, ProjectStage.CONTENT);
      
      // Navigate with audio-url
      const queryParams = new URLSearchParams();
      queryParams.append('project-id', projectId);
      queryParams.append('audio-url', audioUrl);
      navigate(`/create-content?${queryParams.toString()}`);
    } catch (error) {
      console.error('Error processing upload:', error);
      // Fallback: treat as audio upload
      const queryParams = new URLSearchParams();
      queryParams.append('project-id', projectId);
      queryParams.append('audio-url', mediaUrl);
      navigate(`/create-content?${queryParams.toString()}`);
    }
  };

  // Toggle dropdown visibility
  const toggleActionDropdown = (projectId: string) => {
    setActiveActionDropdown(activeActionDropdown === projectId ? null : projectId);
  };

  // Handle project delete
  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      showNotification({
        title: 'Success',
        message: 'Project deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification({
        title: 'Delete Failed',
        message: 'There was an error deleting the project',
        type: 'error'
      });
    }
  };

  // Handle project duplication
  const handleDuplicateProject = async (projectId: string) => {
    try {
      const newProject = await duplicateProject(projectId);
      setProjects([newProject, ...projects]);
      showNotification({
        title: 'Success',
        message: 'Project duplicated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error("Duplication failed:", error);
      showNotification({
        title: 'Duplication Failed',
        message: 'There was an error duplicating the project',
        type: 'error'
      });
    }
  };

  // Handle replacing audio for a project
  const handleReplaceAudio = (projectId: string) => {
    // Store the project ID for use when the file is selected
    replaceAudioRef.current?.setAttribute('data-project-id', projectId);
    // Trigger file selection
    replaceAudioRef.current?.click();
  };

  // Process the new audio file
  const handleReplaceAudioFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    
    const projectId = event.target.getAttribute('data-project-id');
    if (!projectId) {
      console.error("No project ID found for audio replacement");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const audioBlob = event.target.files[0];
      
      // Upload the new audio file
      const uploadedUrl: UploadAudioResponse = await uploadAudio(audioBlob);
      
      if (uploadedUrl) {
        console.log("Replaced audio URL:", uploadedUrl);
        
        // Navigate to the create content editor with the new audio
        const queryParams = new URLSearchParams({ 
          'audio-url': uploadedUrl.file.url, 
          'project-id': uploadedUrl.projectId 
        });
        
        navigate(`/create-content?${queryParams.toString()}`);
        
        showNotification({
          title: 'Success',
          message: 'Audio replaced successfully',
          type: 'success'
        });
      }
    } catch (error) {
      console.error("Audio replacement failed:", error);
      showNotification({
        title: 'Replacement Failed',
        message: 'There was an error replacing the audio file',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      // Clear the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Handle continue working on project
  const handleContinueProject = async (project: Project) => {
    console.log('Continue project:', { 
      projectId: project.id, 
      projectType: project.type, 
      originalVideoUrl: project.originalVideoUrl,
      originalAudioUrl: project.originalAudioUrl,
      finalAudio: project.finalAudio,
      metadata: project.metadata,
      mediaUrl: project.finalAudio || project.originalAudioUrl || project.originalVideoUrl 
    });
    
    try {
      // Update project stage to CONTENT_APP when navigating to content editor
      await updateProjectStage(project.id, ProjectStage.CONTENT_APP);
      
      // Navigate to the appropriate editor - Create Content always uses audio-url
      const queryParams = new URLSearchParams();
      queryParams.append('project-id', project.id);
      
      // For Create Content workflow, prioritize finalAudio, then originalAudioUrl, then originalVideoUrl for audio
      const audioUrl = project.finalAudio || project.originalAudioUrl;
      if (audioUrl) {
        queryParams.append('audio-url', audioUrl);
      }
      
      // Also pass video-url for thumbnail generation - prioritize generatedVideoUrl from CreateContent API
      const videoUrl = project.generatedVideoUrl || project.originalVideoUrl;
      if (videoUrl) {
        queryParams.append('video-url', videoUrl);
      }
      
      // ✅ Add aspect ratio conversion params for cloned projects
      addAspectRatioConversionParams(queryParams, project, '[CREATE CONTENT]');
      
      console.log('Create Content navigation:', `/create-content?${queryParams.toString()}`);
      navigate(`/create-content?${queryParams.toString()}`);
    } catch (error) {
      console.error("Error updating project stage:", error);
      showNotification({
        title: 'Error',
        message: 'Failed to update project stage',
        type: 'error'
      });
      
      // Still navigate even if the stage update fails
      const queryParams = new URLSearchParams();
      queryParams.append('project-id', project.id);
      
      // For Create Content workflow, prioritize finalAudio, then originalAudioUrl, then originalVideoUrl for audio
      const audioUrl = project.finalAudio || project.originalAudioUrl;
      if (audioUrl) {
        queryParams.append('audio-url', audioUrl);
      }
      
      // Also pass video-url for thumbnail generation - prioritize generatedVideoUrl from CreateContent API
      const videoUrl = project.generatedVideoUrl || project.originalVideoUrl;
      if (videoUrl) {
        queryParams.append('video-url', videoUrl);
      }
      
      // ✅ Also add conversion params in error case
      addAspectRatioConversionParams(queryParams, project, '[CREATE CONTENT]');
      
      navigate(`/create-content?${queryParams.toString()}`);
    }
  };

  // Filter projects based on selectedOption, searchTerm, and project type
  const captionProjects = projects.filter(project => {
    // Filter by type
    if (selectedOption === 'audio' && project.type !== 'audio') return false;
    if (selectedOption === 'video') {
      // For video filter, show projects that have any video-related URL
      const hasVideoUrl = project.originalVideoUrl || 
                         project.captionedVideoUrl || 
                         project.generatedVideoUrl;
      
      if (!hasVideoUrl) return false;
    }
    if (selectedOption === 'recent') {
      // Get projects from the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const projectDate = new Date(project.date);
      if (projectDate < sevenDaysAgo) return false;
    }
    
    // Filter by search term
    if (searchTerm && !project.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Calculate counts for each filter
  const audioCount = projects.filter(p => p.type === 'audio').length;
  const videoCount = projects.filter(p => {
    const hasVideoUrl = p.originalVideoUrl || p.captionedVideoUrl || p.generatedVideoUrl;
    return hasVideoUrl;
  }).length;
  const recentCount = projects.filter(p => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const projectDate = new Date(p.date);
    return projectDate >= sevenDaysAgo;
  }).length;

  // Function to get audio duration from URL
  const getAudioDuration = async (audioUrl: string): Promise<string> => {
    try {
      return new Promise((resolve) => {
        const audio = new Audio();
        audio.addEventListener('loadedmetadata', () => {
          const duration = audio.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          resolve(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        });
        
        audio.addEventListener('error', () => {
          resolve('0:00');
        });
        
        audio.src = audioUrl;
      });
    } catch (error) {
      console.error('Error getting audio duration:', error);
      return '0:00';
    }
  };

  // Function to get project duration - prioritizes finalAudio over originalAudioUrl
  const getProjectDuration = async (project: Project): Promise<string> => {
    // If project already has a valid duration (not "00:00" or empty), use it
    if (project.duration && project.duration !== '00:00' && project.duration !== '0:00') {
      return project.duration;
    }
    
    // Otherwise, calculate from audio URL - prioritize finalAudio
    const audioUrl = project.finalAudio || project.originalAudioUrl;
    if (audioUrl) {
      return await getAudioDuration(audioUrl);
    }
    
    return '0:00';
  };

  // State to store calculated durations
  const [projectDurations, setProjectDurations] = useState<Record<string, string>>({});

  // Effect to calculate durations for projects
  useEffect(() => {
    const calculateDurations = async () => {
      const newDurations: Record<string, string> = {};
      
      for (const project of projects) {
        const duration = await getProjectDuration(project);
        newDurations[project.id] = duration;
      }
      
      setProjectDurations(newDurations);
    };

    if (projects.length > 0) {
      calculateDurations();
    }
  }, [projects]);

  // Helper function to get proxied video URL if needed
  const getVideoUrl = (project: Project): string | null => {
    // For CreateContent: prioritize generatedVideoUrl (from CreateContent API), then originalVideoUrl
    const videoUrl = project.generatedVideoUrl || project.originalVideoUrl;
    
    if (!videoUrl) return null;
    
    return videoUrl;
  };

  // Function to format the date in a more readable way
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={styles.outerContainer}>
      <SEO
        title={seoConfig.createContent.title}
        description={seoConfig.createContent.description}
        keywords={seoConfig.createContent.keywords}
        url="https://kingdomstudio.pro/create-content"
      />
      <Navigation />
      <main className={styles.container}>
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.header}>
              <h1 className={styles.title}>
                <FontAwesomeIcon icon={faVideo} className={styles.titleIcon} />
                {t('contentCreation.hero.title')}
              </h1>
              <p className={styles.subtitle}>
                {t('contentCreation.hero.subtitle')}
              </p>
              
              {/* Action Buttons */}
              <div className={styles.headerActions}>
                <Button
                  className={styles.createButton}
                  onClick={handleUploadClick}
                  leftSection={<FontAwesomeIcon icon={faUpload} />}
                  size="lg"
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? t('contentCreation.hero.processing') : t('contentCreation.hero.uploadButton')}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Hidden file inputs */}
        <input
          type="file"
          accept="audio/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        
        {/* Hidden file input for audio replacement */}
        <input
          type="file"
          accept="audio/*"
          ref={replaceAudioRef}
          style={{ display: 'none' }}
          onChange={handleReplaceAudioFileChange}
        />
        
        {/* Dashboard component for showing upload progress */}
        {jobId && (
          <div className={styles.dashboardContainer}>
            <Dashboard jobId={jobId} callBackFunc={processData} displayOption="fullview" />
          </div>
        )}
        
        {/* Projects Section */}
        <div className={styles.projectsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('contentCreation.header.title')}</h2>
            <div className={styles.searchAndFilter}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder={t('contentCreation.projectList.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.filterOptions}>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'all' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('all')}
                >
                  {t('contentCreation.projectList.filters.all')} ({projects.length})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'recent' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('recent')}
                >
                  {t('contentCreation.projectList.filters.recent')} ({recentCount})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'audio' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('audio')}
                >
                  {t('contentCreation.projectList.filters.audio')} ({audioCount})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'video' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('video')}
                >
                  {t('contentCreation.projectList.filters.video')} ({videoCount})
                </button>
              </div>
            </div>
          </div>
          
          {isProjectsLoading ? (
            <div className={styles.loadingContainer}>
              <Loader size="xl" />
              <p className={styles.loadingText}>{t('contentCreation.projectList.loading')}</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p className={styles.errorMessage}>{error}</p>
              <Button onClick={() => window.location.reload()} className={styles.retryButton}>
                Retry
              </Button>
            </div>
          ) : projects.length > 0 ? (
            <div className={styles.projectGrid}>
              {captionProjects.map(project => (
                <div 
                  key={project.id} 
                  className={`${styles.projectCard} 
                    ${activeActionDropdown === project.id ? styles.cardWithActiveDropdown : ''}
                    ${getVideoUrl(project) ? styles.hasGeneratedVideo : ''}
                  `}
                >
                  {getVideoUrl(project) && (
                    <a 
                      href={getVideoUrl(project)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className={styles.videoAvailableBadge}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </a>
                  )}
                  <div className={styles.projectThumbnail}>
                    {getVideoUrl(project) ? (
                      <video 
                        src={getVideoUrl(project)!}
                        className={styles.projectVideo}
                        muted
                        preload="metadata"
                        poster=""
                        onLoadedData={(e) => {
                          const video = e.target as HTMLVideoElement;
                          video.currentTime = 0.1;
                        }}
                        onLoadedMetadata={(e) => {
                          const video = e.target as HTMLVideoElement;
                          video.currentTime = Math.min(1, video.duration * 0.1);
                        }}
                        onError={(e) => {
                          console.error('❌ Video failed to load for project:', project.title);
                          const videoElement = e.target as HTMLVideoElement;
                          const img = document.createElement('img');
                          img.src = project.thumbnail || ((project.type === 'audio' || project.type === 'script') ? 
                            getApiUrl('/public/assets/audio-waveform.svg') : 
                            'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg');
                          img.alt = project.title;
                          img.className = styles.projectThumbnail;
                          img.onerror = () => {
                            img.src = 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                          };
                          videoElement.parentNode?.replaceChild(img, videoElement);
                        }}
                      />
                    ) : (
                      <img 
                        src={
                          project.thumbnail && !project.thumbnail.includes('placeholder.com') 
                            ? project.thumbnail 
                            : (project.type === 'audio' || project.type === 'script') 
                              ? getApiUrl('/public/assets/audio-waveform.svg')
                              : 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg'
                        }
                        alt={project.title}
                        className={styles.projectthumbnail}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (project.type === 'audio' || project.type === 'script') {
                            target.src = getApiUrl('/public/assets/audio-waveform.svg');
                          } else {
                            target.src = 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                          }
                        }} 
                      />
                    )}
                    <div className={styles.projectType}>
                      <FontAwesomeIcon icon={(project.generatedVideoUrl || project.captionedVideoUrl || project.originalVideoUrl) ? faVideo : faMicrophone} />
                    </div>
                    <div className={styles.projectDuration}>
                      <FontAwesomeIcon icon={faClock} />
                      <span>{projectDurations[project.id] || project.duration || '0:00'}</span>
                    </div>
                  </div>
                  
                  <div className={styles.projectInfo}>
                    <h3 className={styles.projectTitle}>{project.title}</h3>
                    <div className={styles.projectMeta}>
                      <span className={styles.projectDate}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {formatDate(project.date)}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.projectActions}>
                    <Button 
                      className={styles.continueButton}
                      onClick={() => handleContinueProject(project)}
                    >
                      Continue
                    </Button>
                    <div className={styles.moreActions}>
                      <button 
                        className={styles.moreButton}
                        onClick={() => toggleActionDropdown(project.id)}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      {activeActionDropdown === project.id && (
                        <div className={`${styles.actionDropdown} ${activeActionDropdown === project.id ? styles.active : ''}`}
                             onClick={(e) => e.stopPropagation()}
                        >
                          {getVideoUrl(project) && (
                            <a 
                              className={styles.actionButton}
                              href={getVideoUrl(project)!}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              <FontAwesomeIcon icon={faDownload} className="fa-download" /> Download Video
                            </a>
                          )}
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleReplaceAudio(project.id)}
                          >
                            <FontAwesomeIcon icon={faUpload} /> Replace Audio
                          </button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDuplicateProject(project.id)}
                          >
                            Duplicate
                          </button>
                          <button className={styles.actionButton}>Share</button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} className="fa-trash" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noProjects}>
              <FontAwesomeIcon icon={faPlus} size="2x" className={styles.noProjectsIcon} />
              <h3>No content projects found</h3>
              <p>Start by uploading an audio file using the button above.</p>
              <Button 
                className={styles.createNewButton}
                onClick={handleUploadClick}
                disabled={isLoading}
                leftSection={<FontAwesomeIcon icon={faUpload} />}
              >
                Upload Audio
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CreateContent;
